'use strict';

// Share composer — the half that both consumers run.
//
// Two surfaces compose an announcement, each in a tab of its sticky
// workbench: the posts console (posts_admin.js) and the publications console
// (publi_admin.js). The character counting, the wire format and the result
// rendering are identical on both.
//
// Plain DOM, deliberately: nothing should have to pull jQuery into a bundle
// to count characters.

/**
 * Update every `.share-cnt` inside `panel` to the current text length.
 *
 * The over-limit state is a class rather than an inline colour. The three
 * hardcoded hexes this replaced (#c00, #060, #b60) could not follow a skin —
 * see css/_share_results.scss.
 */
export function updateCounts(panel) {
  const textarea = panel.querySelector('.share-textarea');
  if (!textarea) return;

  const len = textarea.value.length;

  panel.querySelectorAll('.share-cnt').forEach(span => {
    const limit = parseInt(span.dataset.limit, 10);
    span.textContent = String(len);
    span.classList.toggle('is-over', Number.isFinite(limit) && len > limit);
  });
}

/** The slugs of the platform checkboxes currently ticked inside `panel`. */
export function selectedPlatforms(panel) {
  return Array.from(panel.querySelectorAll('.platform-cb:checked')).map(cb => cb.value);
}

function line(cls, text, linkHref, linkText) {
  const el = document.createElement('div');
  el.className = `share-result share-result--${cls}`;

  el.appendChild(document.createTextNode(text));

  if (linkHref) {
    el.appendChild(document.createTextNode(' '));
    const a = document.createElement('a');
    a.href = linkHref;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = linkText;
    el.appendChild(a);
  }

  return el;
}

/**
 * Render the per-platform outcomes into `target`.
 *
 * Built with createElement/textContent rather than an HTML string: every value
 * here — the platform name, the error text, the share URL — comes back from a
 * third-party API response, and the escaping helper the string version needed
 * is exactly the kind that gets forgotten on the next branch added.
 */
export function renderResults(target, results) {
  target.textContent = '';

  const entries = Object.entries(results || {});
  if (entries.length === 0) {
    const none = document.createElement('span');
    none.className = 'share-result__none';
    none.textContent = 'No platforms selected.';
    target.appendChild(none);
    return;
  }

  entries.forEach(([slug, r]) => {
    const name = slug.charAt(0).toUpperCase() + slug.slice(1);

    if (r.success) {
      target.appendChild(line('ok', `${name} posted.`, r.url, 'view post'));
    } else if (r.requires_manual_action) {
      target.appendChild(
        line('manual', `${name}: paste and submit manually —`, r.share_url, `open ${name}`)
      );
    } else {
      target.appendChild(line('fail', `${name}: ${r.error || 'failed.'}`));
    }
  });
}

/**
 * POST a composed announcement and render whatever comes back.
 *
 * `platforms[]`, with the brackets, is not a stylistic choice: the endpoints
 * read `$_POST['platforms']` as an array, which is the only shape PHP builds
 * one from. It is also exactly what the jQuery `$.ajax` call this replaced put
 * on the wire, so the endpoint sees no change at all.
 *
 * @param {object} opts
 * @param {string} opts.endpoint    URL to POST to
 * @param {object} opts.fields      scalar form fields (ids, text, csrf_token)
 * @param {string[]} opts.platforms platform slugs
 * @param {Element} opts.button     the control to disable while in flight
 * @param {Element} opts.results    where to render the outcome
 * @returns {Promise<void>}
 */
export function postShare({ endpoint, fields, platforms, button, results }) {
  const body = new URLSearchParams();
  Object.entries(fields).forEach(([k, v]) => body.append(k, v));
  platforms.forEach(slug => body.append('platforms[]', slug));

  const label = button ? button.textContent : '';
  if (button) {
    button.disabled = true;
    button.textContent = 'Posting…';
  }
  results.textContent = '';

  return fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
    .then(res => {
      // A CSRF failure re-renders the HTML csrf_error page with HTTP 200 (see
      // AppContext::checkCsrf() / Response::page()), so res.ok alone cannot
      // tell success from failure — check the content type too. Same guard as
      // markTourSeen() in collab_help.js.
      const type = res.headers.get('content-type') || '';
      if (!res.ok || !type.includes('application/json')) {
        throw new Error(`share failed (HTTP ${res.status})`);
      }
      return res.json();
    })
    .then(data => renderResults(results, (data.data && data.data.results) || {}))
    .catch(err => {
      results.textContent = '';
      results.appendChild(
        line('fail', `${err.message}. Nothing was posted — check the server logs.`)
      );
    })
    .finally(() => {
      if (button) {
        button.disabled = false;
        button.textContent = label;
      }
    });
}
